/// Corte da faixa vazia em volta da logo, feito no navegador antes do envio, só quando quem envia
/// escolhe "Sem margens". SVG fica de fora: a margem dele é o viewBox, e desenhá-lo em pixels
/// perderia o vetor.

export type TrimBox = { x: number; y: number; width: number; height: number };
type Rgba = [number, number, number, number];

const ALPHA = 16;
const TOLERANCE = 24;
/// Faixa menor que isso, somando os dois lados, não muda o tamanho aparente: nem oferece o corte.
const MIN_GAIN = 0.04;
const MAX_PIXELS = 40_000_000;

/// Onde está o desenho. O fundo é transparente (os quatro cantos sem cor) ou liso (os quatro cantos
/// da mesma cor); em qualquer outro caso não há margem confiável a cortar e o resultado é nulo.
export function contentBox(
  data: Uint8ClampedArray,
  width: number,
  height: number,
): { box: TrimBox; background: Rgba | null } | null {
  const at = (x: number, y: number): Rgba => {
    const i = (y * width + x) * 4;
    return [data[i]!, data[i + 1]!, data[i + 2]!, data[i + 3]!];
  };
  const corners = [at(0, 0), at(width - 1, 0), at(0, height - 1), at(width - 1, height - 1)];
  const near = (a: Rgba, b: Rgba) =>
    Math.max(Math.abs(a[0] - b[0]), Math.abs(a[1] - b[1]), Math.abs(a[2] - b[2])) <= TOLERANCE;
  let background: Rgba | null;
  if (corners.every((pixel) => pixel[3] < ALPHA)) background = null;
  else if (corners.every((pixel) => pixel[3] >= 255 - ALPHA && near(pixel, corners[0]!)))
    background = corners[0]!;
  else return null;
  let left = width,
    top = height,
    right = -1,
    bottom = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const pixel = at(x, y);
      const ink = background
        ? pixel[3] < 255 - ALPHA || !near(pixel, background)
        : pixel[3] >= ALPHA;
      if (!ink) continue;
      if (x < left) left = x;
      if (x > right) right = x;
      if (y < top) top = y;
      if (y > bottom) bottom = y;
    }
  }
  if (right < 0) return null;
  const box = { x: left, y: top, width: right - left + 1, height: bottom - top + 1 };
  if (box.width > width * (1 - MIN_GAIN) && box.height > height * (1 - MIN_GAIN)) return null;
  return { box, background };
}

/// O Símbolo precisa continuar quadrado: o lado menor do recorte cresce em volta do desenho.
export function squared(box: TrimBox): TrimBox {
  const side = Math.max(box.width, box.height);
  return {
    x: box.x - Math.floor((side - box.width) / 2),
    y: box.y - Math.floor((side - box.height) / 2),
    width: side,
    height: side,
  };
}

export type LogoTrim = {
  file: File;
  width: number;
  height: number;
  originalWidth: number;
  originalHeight: number;
};

/// Lê o arquivo, acha a margem e devolve a versão sem ela, em PNG. Nulo quando não há o que cortar
/// ou o navegador não consegue desenhar a imagem; aí o envio segue com o original.
export async function prepareLogoTrim(file: File, square: boolean): Promise<LogoTrim | null> {
  if (file.type === 'image/svg+xml' || /\.svg$/iu.test(file.name)) return null;
  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    if (!width || !height || width * height > MAX_PIXELS) return null;
    const source = document.createElement('canvas');
    source.width = width;
    source.height = height;
    const reader = source.getContext('2d', { willReadFrequently: true })!;
    reader.drawImage(bitmap, 0, 0);
    const found = contentBox(reader.getImageData(0, 0, width, height).data, width, height);
    if (!found) return null;
    const box = square ? squared(found.box) : found.box;
    const target = document.createElement('canvas');
    target.width = box.width;
    target.height = box.height;
    const writer = target.getContext('2d')!;
    // Fundo liso continua liso onde o quadrado passa da imagem; transparente continua vazio.
    if (found.background) {
      const [r, g, b] = found.background;
      writer.fillStyle = `rgb(${r} ${g} ${b})`;
      writer.fillRect(0, 0, box.width, box.height);
    }
    writer.drawImage(bitmap, -box.x, -box.y);
    const blob = await new Promise<Blob | null>((resolve) => target.toBlob(resolve, 'image/png'));
    if (!blob) return null;
    return {
      file: new File([blob], file.name.replace(/\.[^.]+$/u, '') + '.png', { type: 'image/png' }),
      width: box.width,
      height: box.height,
      originalWidth: width,
      originalHeight: height,
    };
  } catch {
    return null;
  }
}
